import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Gallery.css';
import './Detail.css';
import './Info.css';
import PROJECTS, { getProjectByUrl, projectUrl, indexOfProject, projectImageUrl, TOTAL_PROJECTS, isVisible } from '../utils/projectsData';
import { HOVER_COLORS } from '../utils/hoverMelody';
import Plate from './Plate';
import Lightbox from './Lightbox';
import LettersPlate from './LettersPlate';
import LetterPrint from './LetterPrint';
import ThingsGrid from './ThingsGrid';


/* A paragraph is either a plain string or an array of pieces, where a piece
   written as { b: 'like this' } comes out bold. Keeps the data file readable
   and stops any HTML getting into it. */
function Para({ value }) {
  if (!Array.isArray(value)) return value;
  return value.map((part, i) =>
    typeof part === 'string'
      ? part
      : <span className="bold-text" key={i}>{part.b}</span>
  );
}

export default function ThingDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const project = getProjectByUrl(slug);
  const currentId = project ? indexOfProject(project) : 0;

  const [showMetadata, setShowMetadata] = useState(false);
  const [lightbox, setLightbox] = useState(null);   // index into the image-only list

  // hero first, then the rest of the project's media in the order the
  // original page had it (images and video embeds interleaved)
  const plates = useMemo(() => {
    if (!project) return [];
    const media = project.media || [];
    // A hero named tile.* is the grid tile and nothing else. It never opens the
    // project page. Anything else in `hero` is a real first image and still does.
    const isTile = /^tile\./i.test(project.hero || '');
    const live = media.some((m) => m.type === 'letters' || m.type === 'things'
      || m.type === 'letterprint');
    const head = project.hero && !isTile && !live ? [{ type: 'img', src: project.hero }] : [];
    return head.concat(media);
  }, [project]);

  // the viewer holds stills only; videos stay on the page where they play
  const stills = useMemo(
    () => plates.filter((m) => m.type === 'img').map((m) => projectImageUrl(project, m.src)),
    [plates, project]
  );

  useEffect(() => { setShowMetadata(false); setLightbox(null); }, [currentId]);


  const credits = project ? (project.credits || []) : [];

  /* Step over anything hidden, so the arrows never land on a project that
     isn't being shown yet. */
  const step = useCallback((from, dir) => {
    for (let i = from + dir; i >= 1 && i <= TOTAL_PROJECTS; i += dir) {
      if (isVisible(PROJECTS[i - 1])) return i;
    }
    return null;
  }, []);

  const prevId = step(currentId, -1);
  const nextId = step(currentId, 1);

  const handlePreviousClick = useCallback(() => {
    if (prevId) navigate(projectUrl(PROJECTS[prevId - 1]));
  }, [prevId, navigate]);

  const handleNextClick = useCallback(() => {
    if (nextId) navigate(projectUrl(PROJECTS[nextId - 1]));
  }, [nextId, navigate]);

  useEffect(() => {
    function handleKeyPress(event) {
      if (event.key === 'ArrowLeft') handlePreviousClick();
      else if (event.key === 'ArrowRight') handleNextClick();
    }
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePreviousClick, handleNextClick]);

  if (!project) return <div className="home-container"><div className="body-text">Not found.</div></div>;


  return (
    <div>
      <div className="home-container">
        <div className="detail-intro">
          {(Array.isArray(project.overview) ? project.overview : [project.overview]).map((para, i) => (
            <div className="body-text" key={i} style={i ? { marginTop: '16px' } : undefined}>
              <Para value={para} />
            </div>
          ))}
        </div>

        {project.links && project.links.length > 0 && (
          <ul className="info-links detail-links">
            {project.links.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="title-link-secondary"
                  target="_blank"
                  rel="noreferrer"
                  style={{ '--hover-color': HOVER_COLORS[currentId % HOVER_COLORS.length] }}
                >
                  <span className="title-text-secondary">{link.label}</span>
                </a>
              </li>
            ))}
          </ul>
        )}

        {plates.map((m, i) => {
          // a block of copy sitting between images, same type as the intro
          if (m.type === 'text') {
            return (
              <div className="detail-intro detail-interlude" key={'t' + i}>
                {m.paras.map((para, j) => (
                  <div className="body-text" key={j} style={j ? { marginTop: '16px' } : undefined}>
                    <Para value={para} />
                  </div>
                ))}
              </div>
            );
          }
          if (m.type === 'letters') {
            return <LettersPlate key={'letters' + i} />;
          }
          if (m.type === 'letterprint') {
            return <LetterPrint key={'letterprint' + i} />;
          }
          if (m.type === 'things') {
            return <ThingsGrid key={'things' + i} />;
          }
          if (m.type === 'video') {
            return (
              <div className="main-image" key={m.src + i}>
                <div className="video-embed">
                  <iframe
                    src={m.src}
                    title=""
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            );
          }
          const url = projectImageUrl(project, m.src);
          return (
            <Plate
              key={m.src + i}
              src={url}
              alt=""
              eager={i === 0}
              onOpen={() => setLightbox(stills.indexOf(url))}
            />
          );
        })}

        {/* the caption slot the original template had. Empty for now, but it
            keeps the space between the last image and the Credits toggle. */}
        <div className="caption-text" style={{ marginBottom: '28px' }} />

        <div
          className="bold-text body-text trait-toggle"
          onClick={() => setShowMetadata(!showMetadata)}
        >
          <span className="title-text title-link">
            {'Credits'.split('').map((letter, index) => (
              <span key={index} className="title-letter" style={{ '--letter-index': index }}>
                {letter === ' ' ? ' ' : letter}
              </span>
            ))}
          </span>
        </div>

        {showMetadata && credits.length > 0 && (
          <div className="credits-table">
            <div className="credits-row credits-head">
              <div className="credits-cell">Role</div>
              <div className="credits-cell">Name</div>
            </div>
            {credits.map(([role, name], i) => (
              <div className="credits-row" key={i}>
                <div className="credits-cell">{role}</div>
                <div className="credits-cell">{name}</div>
              </div>
            ))}
          </div>
        )}

        <div className="arrow-container">
          <button
            onClick={handlePreviousClick}
            disabled={!prevId}
            className="arrow-button"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <button
            onClick={handleNextClick}
            disabled={!nextId}
            className="arrow-button"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>

      {lightbox !== null && (
        <Lightbox
          images={stills}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
